import { useCombinedRefs } from '@visiba/react-std';
import { rem } from '@visiba/std';
import { ForwardedRef, forwardRef, useRef } from 'react';
import styled from 'styled-components';
import { HeadlessButton } from '../../button';
import { Icon } from '../../icon';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { InputProps, elements as inputElements } from '../base/input.component';
import { Label } from '../base/label.component';
import { theme as commonTheme } from '../common_input.theme';
import { useInput } from '../use_input.hook';
import { theme } from './input_time.theme';

export interface InputTimeRootProps extends InputProps {
	/**
	 * The label of the input field.
	 */
	label: string;

	name?: string;

	/**
	 * Use to visually hide the label.
	 */
	hideLabel?: boolean;

	/**
	 * Use to add a descriptive text below the input field.
	 */
	helperText?: string;

	/**
	 * It's mandatory if `invalid` is set to `true` and only visible in that case
	 * too. The purpose with this is to provide a explanation why the input field is
	 * `invalid`. This text will replace the `helperText`, right below the text
	 * area.
	 */
	invalidText?: string;
	defaultValue?: string;

	type?: never;
	description?: string;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
	`,
	label: styled(Label)`
		margin-bottom: ${commonTheme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	input: styled(inputElements.input)`
		padding-left: ${theme.getValue('iconPaddingLeft')};

		&::-webkit-calendar-picker-indicator {
			display: none;
		}
	`,
	iconButton: styled(HeadlessButton)`
		all: unset;
		position: absolute;
		top: 50%;
		left: ${rem(1)};
		transform: translateY(-50%);
		color: ${theme.getValue('iconColor')};
		width: ${theme.getValue('iconPaddingLeft')};
		height: calc(100% - ${rem(2)});
		display: flex;
		align-items: center;
		justify-content: center;
	`,
	icon: styled(Icon)`
		width: ${theme.getValue('iconSize')};
		height: ${theme.getValue('iconSize')};
	`,
};

export const CellulaInputTimeRoot = forwardRef(function CellulaInputTimeRoot(
	{
		id,
		name,
		label,
		hideLabel,
		required,
		invalid,
		invalidText,
		helperText,
		defaultValue,
		readOnly,
		onChange,
		onBlur,
		description,
		optional,
		...rest
	}: InputTimeRootProps,
	ref: ForwardedRef<HTMLInputElement>,
): JSX.Element {
	const {
		props: inputProps,
		labelProps,
		helpTextProps,
		descriptionProps,
	} = useInput(
		{
			id,
			name,
			label,
			hideLabel,
			required,
			invalid,
			invalidText,
			helperText,
			defaultValue,
			readOnly,
			onChange,
			onBlur,
			description,
			optional,
		},
		ref,
	);
	const inputRef = useRef<HTMLInputElement>(null);
	const combinedRefs = useCombinedRefs(inputProps.ref, inputRef);

	function handleIconClick(): void {
		if (inputRef.current) {
			inputRef.current.showPicker();
		}
	}

	return (
		<elements.container>
			{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
			<elements.label {...labelProps} />
			<elements.description {...descriptionProps} />

			<inputElements.container>
				<elements.iconButton onClick={handleIconClick} tabIndex={-1}>
					<elements.icon type='clock' />
				</elements.iconButton>
				<elements.input {...rest} {...inputProps} ref={combinedRefs} type='time' />
			</inputElements.container>

			<HelpText {...helpTextProps} />
		</elements.container>
	);
});
